import { useSeo, setNoindex } from "../lib/seo";
import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Dna, GitBranch, Loader2 } from "lucide-react";
import { api, formatApiError, fmtPrice, optimizeImg } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { PolicyDialog } from "../components/PolicyDialog";

const Placard = ({ label, children }) => (
  <div className="border border-white/10 rounded-xl p-6 md:p-8 bg-qbsurface">
    <span className="text-xs tracking-[0.2em] uppercase text-qbfg/40">{label}</span>
    <div className="mt-4">{children}</div>
  </div>
);

const ParentCard = ({ role, name, id, morphs, testid }) => (
  <div className="border border-white/10 rounded-lg p-4 bg-qbbg/40 text-center">
    <span className="text-xs uppercase tracking-[0.2em] text-qbfg/40">{role}</span>
    {id ? (
      <Link to={`/boa/${id}`} data-testid={testid} className="mt-2 block text-qbprimary hover:underline font-medium truncate">{name || "View parent"} ↗</Link>
    ) : (
      <p className="mt-2 text-qbfg font-medium truncate">{name || "—"}</p>
    )}
    <div className="flex flex-wrap gap-1 mt-2 justify-center">
      {(morphs || []).map((m) => <span key={m} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-qbfg/60">{m}</span>)}
    </div>
  </div>
);

export default function BoaDetail() {
  const { id } = useParams();
  const [boa, setBoa] = useState(null);
  const [morphs, setMorphs] = useState([]);
  const [active, setActive] = useState(0);
  const [buying, setBuying] = useState(false);
  const [inq, setInq] = useState({ name: "", email: "", message: "" });
  const [inqLoading, setInqLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get(`/boas/${id}`).then((r) => { setBoa(r.data); setActive(0); }).catch(() => setBoa(false));
    api.get("/morphs").then((r) => setMorphs(r.data)).catch(() => {});
  }, [id]);

  const geneList = boa && boa !== false ? boa.morphs || [] : [];
  const hetList = boa && boa !== false ? boa.het || [] : [];
  const fullGenes = [...geneList, ...hetList.map((h) => `het ${h}`)].join(" ");
  let titleGenes = geneList.join(" ");
  if (boa && boa !== false && `${boa.name} — ${titleGenes} Boa | Quantum Boas`.length > 65) titleGenes = geneList.slice(0, 2).join(" ");
  const seoTitle = boa && boa !== false ? `${boa.name} — ${titleGenes ? `${titleGenes} ` : ""}Boa | Quantum Boas` : undefined;
  const what = [fullGenes, boa && boa !== false ? (boa.sex || "").toLowerCase() : ""].filter(Boolean).join(" ");
  const seoDesc = boa && boa !== false
    ? `Meet ${boa.name}, a${what ? ` ${what}` : ""} boa constrictor${boa.year ? ` (${boa.year})` : ""} in the Quantum Boas collection. View photos, documented genetics and parentage.`
    : undefined;
  useSeo({ title: seoTitle, description: seoDesc, image: boa && boa !== false ? boa.images?.[0] : undefined });
  useEffect(() => {
    if (boa === false) { document.title = "Animal Not Found | Quantum Boas"; setNoindex(); }
  }, [boa]);

  const [policyOpen, setPolicyOpen] = useState(false);
  const pendingRef = useRef(null);
  const requirePolicy = (fn) => { pendingRef.current = fn; setPolicyOpen(true); };
  const acceptPolicy = () => { setPolicyOpen(false); const fn = pendingRef.current; pendingRef.current = null; if (fn) fn(); };

  const runCheckout = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", { boa_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBuying(false);
    }
  };

  const doSubmitInquiry = async () => {
    setInqLoading(true);
    try {
      const { data } = await api.post("/inquiries", { boa_id: id, ...inq });
      toast.success(data.message);
      setOpen(false);
      setInq({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setInqLoading(false);
    }
  };
  const submitInquiry = (e) => { e.preventDefault(); setOpen(false); requirePolicy(doSubmitInquiry); };

  if (boa === null) return <div className="pt-40 text-center text-qbfg/40">Loading…</div>;
  if (boa === false) return (
    <div className="pt-40 text-center">
      <p className="text-qbfg/50 mb-4">Animal not found.</p>
      <Link to="/shop" className="text-qbprimary">Back to collection</Link>
    </div>
  );

  const morphInfo = (name) => morphs.find((m) => m.name === name);

  return (
    <div className="grain pt-28 pb-24 px-6 md:px-10 min-h-screen">
      <div className="max-w-[1400px] mx-auto">
        <Link to="/shop" data-testid="back-to-shop" className="inline-flex items-center gap-2 text-qbfg/50 hover:text-qbfg mb-8 text-sm transition-colors">
          <ArrowLeft className="w-4 h-4" /> Collection
        </Link>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Gallery */}
          <div>
            <motion.div
              key={active}
              initial={{ opacity: 0.3 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
              className="relative aspect-[4/5] rounded-xl overflow-hidden border border-white/10 bg-qbsurface"
            >
              <img src={optimizeImg(boa.images?.[active], 1100)} alt={boa.name} data-testid="boa-main-image" decoding="async" fetchpriority="high" className="w-full h-full object-cover" />
              <div className="absolute inset-0 shadow-[inset_0_0_120px_40px_rgba(0,0,0,0.6)]" />
            </motion.div>
            {boa.images?.length > 1 && (
              <div className="flex gap-3 mt-3">
                {boa.images.map((img, i) => (
                  <button key={i} onClick={() => setActive(i)}
                    className={`w-20 h-24 rounded-lg overflow-hidden border transition-colors ${i === active ? "border-qbprimary" : "border-white/10"}`}>
                    <img src={optimizeImg(img, 200)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full border ${
                boa.status === "available" ? "text-qbprimary border-qbprimary/40" :
                boa.status === "reserved" ? "text-amber-400 border-amber-400/40" : "text-qbfg/40 border-white/15"}`}>
                {boa.status}
              </span>
              <span className="text-xs text-qbfg/50">{boa.species}</span>
            </div>
            <h1 className="font-display text-5xl md:text-6xl leading-[1.05] pb-1">{boa.name}</h1>
            <div className="flex items-baseline gap-4 mt-5">
              <span className="text-3xl text-qbprimary font-body">{fmtPrice(boa.price)}</span>
              <span className="text-qbfg/50 text-sm">{boa.sex} · {boa.year} · {boa.weight}</span>
            </div>
            <p className="text-qbfg/65 leading-relaxed mt-6">{boa.description}</p>

            {/* Morph chips */}
            <div className="flex flex-wrap gap-2 mt-6">
              {(boa.morphs || []).map((m) => (
                <span key={m} className="text-sm px-3 py-1.5 rounded-full bg-qbprimary/10 border border-qbprimary/30 text-qbprimary">{m}</span>
              ))}
              {(boa.het || []).map((h) => (
                <span key={h} data-testid={`het-chip-${h}`} className="text-sm px-3 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/40 text-amber-300">het {h}</span>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              {boa.status !== "sold" && boa.price > 0 && (
                <button onClick={() => requirePolicy(runCheckout)} disabled={buying} data-testid="buy-now-btn"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-qbprimary bg-qbprimary text-qbbg font-medium hover:bg-transparent hover:text-qbprimary transition-colors disabled:opacity-60">
                  {buying ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : `Purchase · ${fmtPrice(boa.price)}`}
                </button>
              )}
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <button data-testid="inquire-btn"
                    className="flex-1 px-7 py-4 rounded-full border border-white/25 text-qbfg hover:border-qbprimary hover:text-qbprimary transition-colors">
                    {boa.status === "sold" ? "Join waitlist" : "Inquire / Reserve"}
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-qbsurface border-white/10 text-qbfg">
                  <DialogHeader><DialogTitle className="font-display text-2xl">Inquire about {boa.name}</DialogTitle></DialogHeader>
                  <form onSubmit={submitInquiry} className="space-y-4 mt-2" data-testid="inquiry-form">
                    <input required placeholder="Your name" value={inq.name} data-testid="inquiry-name"
                      onChange={(e) => setInq({ ...inq, name: e.target.value })}
                      className="w-full bg-qbbg border border-white/15 rounded-lg px-4 py-3 outline-none focus:border-qbprimary transition-colors" />
                    <input required type="email" placeholder="Email" value={inq.email} data-testid="inquiry-email"
                      onChange={(e) => setInq({ ...inq, email: e.target.value })}
                      className="w-full bg-qbbg border border-white/15 rounded-lg px-4 py-3 outline-none focus:border-qbprimary transition-colors" />
                    <textarea rows={3} placeholder="Message (optional)" value={inq.message} data-testid="inquiry-message"
                      onChange={(e) => setInq({ ...inq, message: e.target.value })}
                      className="w-full bg-qbbg border border-white/15 rounded-lg px-4 py-3 outline-none focus:border-qbprimary transition-colors resize-none" />
                    <button type="submit" disabled={inqLoading} data-testid="inquiry-submit"
                      className="w-full py-3 rounded-full bg-qbprimary text-qbbg font-medium hover:bg-qbfg transition-colors disabled:opacity-60">
                      {inqLoading ? "Sending…" : "Send inquiry"}
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>

        {/* Placards */}
        <div className="grid md:grid-cols-2 gap-5 mt-16">
          <Placard label="Parentage history">
            <div className="flex items-center gap-2 mb-6 text-qbprimary"><GitBranch className="w-4 h-4" /><span className="font-display text-2xl">Family tree</span></div>

            {/* Parents row */}
            <div className="grid grid-cols-2 gap-4" data-testid="family-tree">
              <ParentCard role="Sire" name={boa.lineage?.sire} id={boa.lineage?.sire_id} morphs={boa.lineage?.sire_morphs} testid="sire-parent-link" />
              <ParentCard role="Dam" name={boa.lineage?.dam} id={boa.lineage?.dam_id} morphs={boa.lineage?.dam_morphs} testid="dam-parent-link" />
            </div>

            {/* Connectors: two verticals from each parent, joined by a horizontal, then one vertical down to the offspring */}
            <div className="relative h-8" aria-hidden="true">
              <div className="absolute top-0 left-1/4 h-4 w-px bg-white/20" />
              <div className="absolute top-0 right-1/4 h-4 w-px bg-white/20" />
              <div className="absolute top-4 left-1/4 right-1/4 h-px bg-white/20" />
              <div className="absolute top-4 left-1/2 h-4 w-px bg-white/20" />
            </div>

            {/* Offspring (this animal) */}
            <div className="border border-qbprimary/40 rounded-lg p-4 bg-qbprimary/5 max-w-[60%] mx-auto text-center" data-testid="offspring-card">
              <span className="text-xs uppercase tracking-[0.2em] text-qbprimary/70">This animal</span>
              <p className="mt-2 text-qbfg font-display text-lg">{boa.name}</p>
              <div className="flex flex-wrap gap-1 mt-2 justify-center">
                {(boa.morphs || []).map((m) => <span key={m} className="text-[11px] px-2 py-0.5 rounded-full bg-qbprimary/10 text-qbprimary">{m}</span>)}
              </div>
            </div>

            {boa.lineage?.litter_date && <p className="text-sm text-qbfg/50 mt-5">Litter: {boa.lineage.litter_date}</p>}
            {boa.lineage?.notes && <p className="text-sm text-qbfg/60 mt-2 italic">{boa.lineage.notes}</p>}
          </Placard>

          <Placard label="Genetics explained">
            <div className="flex items-center gap-2 mb-5 text-qbprimary"><Dna className="w-4 h-4" /><span className="font-display text-2xl">What each morph does</span></div>
            <div className="space-y-4">
              {(boa.morphs || []).map((m) => {
                const info = morphInfo(m);
                return (
                  <div key={m} className="border-l-2 pl-4" style={{ borderColor: info?.accent || "#00F260" }}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-qbfg">{m}</span>
                      {info && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 text-qbfg/50">{info.trait_type}</span>}
                    </div>
                    <p className="text-sm text-qbfg/55 mt-1 leading-relaxed">{info?.description || "Genetic trait carried by this animal."}</p>
                  </div>
                );
              })}
              <Link to="/genetics" className="inline-block text-sm text-qbprimary mt-2 hover:underline">Full genetics encyclopedia →</Link>
            </div>
          </Placard>
        </div>
      </div>
      <PolicyDialog open={policyOpen} onClose={() => setPolicyOpen(false)} onAccept={acceptPolicy} />
    </div>
  );
}
